import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ArrowRight, Menu, X, Sun, Moon } from 'lucide-react';
import logo from '../assets/botibonga_logo.png';
import useTheme from '../hooks/useTheme';
import { trackEvent, appendUTMsToUrl } from '../services/analytics';

const APP_URL = 'https://app.botibonga.com';

const navLinks = [
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Stories', href: '#testimonials' },
  { label: 'Trust', href: '#trust' },
  { label: 'Contact', href: '#contact' },
];

function handleNavCtaClick(label) {
  return (e) => {
    trackEvent('cta_click', { location: 'navbar', label });
    const target = appendUTMsToUrl(APP_URL);
    if (target !== APP_URL) {
      e.currentTarget.href = target;
    }
  };
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const isMinimal = location.pathname.startsWith('/lp/');

  return (
    <nav
      aria-label="Main navigation"
      className="fixed top-3 sm:top-5 left-0 right-0 z-50 px-3 sm:px-6"
    >
      <div
        className={`max-w-5xl mx-auto ${mobileOpen ? 'rounded-3xl' : 'rounded-full'}`}
        style={{
          background: 'var(--bg-navbar)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border: '1px solid var(--border-navbar)',
          boxShadow: 'var(--shadow-navbar)',
        }}
      >
        <div
          className={`flex items-center justify-between h-14 pl-4 pr-2 sm:pl-6 sm:pr-2.5 ${
            isMinimal ? '' : 'lg:grid lg:grid-cols-[1fr_auto_1fr]'
          }`}
        >
          {/* Left — Logo */}
          {isMinimal ? (
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img src={logo} alt="Botibonga" className="w-7 h-7 rounded-md" />
              <span className="font-bold text-base" style={{ color: 'var(--text-heading)' }}>
                Botibonga
              </span>
            </Link>
          ) : (
            <a href="#home" className="flex items-center gap-2 shrink-0 lg:justify-self-start">
              <img src={logo} alt="Botibonga" className="w-7 h-7 rounded-md" />
              <span className="font-bold text-base" style={{ color: 'var(--text-heading)' }}>
                Botibonga
              </span>
            </a>
          )}

          {/* Center — Links (desktop, hidden on campaign pages) */}
          {!isMinimal && (
            <div className="hidden lg:flex items-center gap-7 lg:justify-self-center">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-sm font-medium transition-colors duration-200 whitespace-nowrap"
                  style={{ color: 'var(--text-body)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-accent)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-body)')}
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}

          {/* Right — Theme toggle + CTA buttons (desktop) */}
          <div className="hidden lg:flex items-center gap-2 lg:justify-self-end">
            <div className="relative group">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full transition-colors duration-200"
                style={{ color: 'var(--text-body)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--text-accent)';
                  e.currentTarget.style.background = 'var(--bg-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--text-body)';
                  e.currentTarget.style.background = 'transparent';
                }}
                aria-label="Toggle theme"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <span
                className="absolute -bottom-9 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
                style={{
                  background: 'var(--bg-tooltip)',
                  color: 'var(--text-tooltip)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                {isDark ? 'Light mode' : 'Dark mode'}
              </span>
            </div>
            {!isMinimal && (
              <a
                href={APP_URL}
                onClick={handleNavCtaClick('Log In')}
                className="text-sm font-medium px-4 py-2 rounded-full transition-colors duration-200 whitespace-nowrap shrink-0"
                style={{
                  color: 'var(--text-heading)',
                  border: '1px solid var(--border-card)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                Log In
              </a>
            )}
            <a
              href={APP_URL}
              onClick={handleNavCtaClick('Get Started Free')}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full text-white transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] whitespace-nowrap shrink-0"
              style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                boxShadow: 'var(--shadow-btn-sm)',
              }}
            >
              Get Started Free
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Mobile: theme toggle + (menu button OR sign-up button on minimal) */}
          <div className="lg:hidden flex items-center gap-1">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full transition-colors duration-200"
              style={{ color: 'var(--text-body)' }}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            {isMinimal ? (
              <a
                href={APP_URL}
                onClick={handleNavCtaClick('Get Started Free')}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2 rounded-full text-white transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  boxShadow: 'var(--shadow-btn-sm)',
                }}
              >
                Get Started
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            ) : (
              <button
                className="p-2 rounded-full transition-colors duration-200"
                style={{ color: 'var(--text-body)' }}
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Toggle menu"
                aria-expanded={mobileOpen}
                aria-controls="mobile-nav"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile menu — extends the pill downward as one connected surface */}
        {!isMinimal && mobileOpen && (
          <div
            id="mobile-nav"
            className="lg:hidden px-3 pb-3 pt-1"
          >
            <div
              className="border-t pt-3 space-y-0.5"
              style={{ borderColor: 'var(--border-card)' }}
            >
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="block px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                  style={{ color: 'var(--text-body)' }}
                  onClick={() => setMobileOpen(false)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--text-accent)';
                    e.currentTarget.style.background = 'var(--bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--text-body)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {link.label}
                </a>
              ))}
            </div>
            <div className="mt-3 flex flex-col gap-2 px-1">
              <a
                href={APP_URL}
                onClick={handleNavCtaClick('Log In')}
                className="text-sm font-medium px-4 py-2.5 rounded-full text-center transition-colors duration-200"
                style={{
                  color: 'var(--text-heading)',
                  border: '1px solid var(--border-card)',
                }}
              >
                Log In
              </a>
              <a
                href={APP_URL}
                onClick={handleNavCtaClick('Get Started Free')}
                className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-full text-white transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  boxShadow: 'var(--shadow-btn-sm)',
                }}
              >
                Get Started Free
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
